import React, { useRef, useState, useEffect, CSSProperties } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import invariant from "tiny-invariant";
import { DragHandle } from "@mui/icons-material";
import styled from "@emotion/styled";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { motion } from "framer-motion";
import { getAgeByDate, formatCurrencyV2 } from "../../list-manager/functions";
import { limitString } from "../../../../../util";

type Props = {
  list: List;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

export default function ListItem({
  list,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<boolean>(false); // NEW
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    const element = ref.current;

    invariant(element, "element is not defined");

    if (!element) return;
    draggable({
      element,
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    });

    dropTargetForElements({
      element,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
      getData: () => ({ ...list }),
    });
  }, []);

  const style: CSSProperties = {};
  const cardStyle: CSSProperties = {};
  if (dragging) {
    style.backgroundColor = "#f3f3f3";
    cardStyle.border = "2px solid #4570ff";
  }

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      layout
      transition={{ duration: 0.25 }}
      ref={ref}
      style={{ borderTopColor: isDraggedOver ? "#336699" : "transparent" }}
    >
      <ItemWrapper
        ref={ref}
        style={{ borderTopColor: isDraggedOver ? "#336699" : "transparent" }}
      >
        <Stack flexDirection="row" className="card" width={"100%"} gap={3}>
          <Stack alignItems="center">
            <IconButton
              onClick={onMoveUp}
              disabled={isFirst}
              sx={{ width: 32, height: 32, padding: 0 }}
            >
              <ArrowDropUp sx={{ fontSize: 28 }} />
            </IconButton>
            <DragHandle />

            <IconButton
              onClick={onMoveDown}
              disabled={isLast}
              sx={{ width: 32, height: 32, padding: 0 }}
            >
              <ArrowDropDown sx={{ fontSize: 28 }} />
            </IconButton>
          </Stack>
          <Stack width={"100%"} gap={1} paddingTop={1}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <div className="number-wrapper">
                <Typography sx={{ fontSize: "0.9rem" }}>{list.rank}</Typography>
              </div>
              <Typography sx={{ fontSize: "0.9em" }}>
                {limitString(list.name, 30)}
              </Typography>
            </Stack>
            <Stack flexDirection="row" alignItems="center">
              <Typography sx={{ fontSize: "0.7rem", color: "gray" }}>
                Contacts: {list.count}
              </Typography>
              <Typography
                sx={{ fontSize: "1rem", padding: "0 8px", color: "lightgray" }}
              >
                {"|"}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "gray" }}>
                Age: {getAgeByDate(list.start_utc)}
              </Typography>
              <Typography
                sx={{ fontSize: "1rem", padding: "0 8px", color: "lightgray" }}
              >
                {"|"}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "gray" }}>
                Total Overdue: {formatCurrencyV2(list?.total_overdue || 0)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </ItemWrapper>
    </motion.div>
  );
}

const ItemWrapper = styled.div`
  border-top: 2px solid transparent;
  transition: all 300ms ease-in-out;
  position: relative;
  padding: 3px 0;
  width: 98%;

  .card:hover {
    background-color: #f3f3f3;
    cursor: grab;
  }

  .card {
    border-radius: 8px;
    border: 1px solid #0003;
    padding: 5px;
  }

  .card .MuiSvgIcon-root {
    opacity: 0.6;
    transition: opacity 0.2s;
  }
  .card .MuiSvgIcon-root:hover {
    opacity: 1;
  }

  .number-wrapper {
    border-radius: 50%;
    aspect-ratio: 1;
    background: rgba(0, 0, 0, 0.05);
    width: 25px;
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;

    text-align: center;
  }
`;
